import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_DataRouterContext, useBlocker, useNavigate } from 'react-router-dom';

/**
 * Keeps unsaved edits from being lost silently (EDIT-05, EQ-20). While `dirty`:
 * - closing or reloading the tab asks the browser's own "Leave site?" question;
 * - leaving the page in the app - a link, the browser Back or Forward button, or code that
 *   navigates - is held, and `pending` is set so the page can show ConfirmDiscardModal
 *   (Discard Changes / Keep Editing);
 * - in-page actions (leave edit mode, switch tab) go through `guard(action)`.
 *
 * In the app, which runs on a data router, React Router's blocker holds the navigation. Under a
 * plain router (tests, embedded pages) in-app link clicks are held instead; Back is not.
 */
export function useUnsavedChangesGuard(dirty: boolean) {
  const navigate = useNavigate();
  const inDataRouter = useContext(UNSAFE_DataRouterContext) != null;
  const [pending, setPending] = useState<(() => void) | null>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Only leaving the page is blocked here: changes to the query string on the same page (the
  // ?tab= switch) are guarded by the page itself through `guard`.
  // eslint-disable-next-line react-hooks/rules-of-hooks -- the router type is fixed for the life of the tree
  const blocker = inDataRouter
    ? useBlocker(({ currentLocation, nextLocation }) => dirtyRef.current && currentLocation.pathname !== nextLocation.pathname)
    : null;

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    if (inDataRouter) return () => window.removeEventListener('beforeunload', onBeforeUnload);

    const onClick = (event: MouseEvent) => {
      if (!dirtyRef.current || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const here = window.location.pathname + window.location.search;
      const there = url.pathname + url.search;
      if (there === here) return;
      event.preventDefault();
      event.stopPropagation();
      setPending(() => () => navigate(there + url.hash));
    };
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, inDataRouter, navigate]);

  /** Runs `action` now when nothing is unsaved; otherwise asks first. */
  const guard = useCallback((action: () => void) => {
    if (dirtyRef.current) setPending(() => action);
    else action();
  }, []);

  const blocked = blocker?.state === 'blocked';

  const discard = useCallback(() => {
    if (blocker?.state === 'blocked') {
      dirtyRef.current = false;
      blocker.proceed();
      return;
    }
    const action = pending;
    setPending(null);
    action?.();
  }, [blocker, pending]);

  const keepEditing = useCallback(() => {
    if (blocker?.state === 'blocked') blocker.reset();
    setPending(null);
  }, [blocker]);

  return { guard, pending: pending !== null || blocked, discard, keepEditing };
}
